import type { StatusTone } from '@afrilink/ui';

/**
 * Presentation helpers for the two hosting-state columns introduced by
 * Slice 2 of the suspension remediation (2026-09-10):
 *
 *   hosting_state          OBSERVED — what WHM last told us
 *   hosting_desired_state  DESIRED  — what the books say it should be
 *   books_vs_server        derived in v_admin_hosting_accounts
 *
 * Kept next to dunningPolicy.ts because they answer the same staff question:
 * "what is true about this account right now, and does the server agree?"
 */

export type HostingState = 'unknown' | 'active' | 'suspended' | 'terminated';
export type HostingDesiredState = 'active' | 'suspended';
export type BooksVsServer =
  | 'unverified'
  | 'agree'
  | 'converging'
  | 'books_suspended_server_live'
  | 'server_suspended_books_active';

export function hostingStateMeta(state: string | null | undefined): {
  label: string;
  tone: StatusTone;
  detail: string;
} {
  switch (state) {
    case 'active':
      return { label: 'Live', tone: 'active', detail: 'WHM last reported this account as not suspended.' };
    case 'suspended':
      return { label: 'Suspended', tone: 'failed', detail: 'WHM last reported this account as suspended.' };
    case 'terminated':
      return { label: 'Removed', tone: 'draft', detail: 'The account no longer exists on the server.' };
    default:
      return {
        label: 'Unverified',
        tone: 'pending',
        detail: 'No observation yet — the nightly reconcile (01:30 UTC) or the next WHM job will stamp it.',
      };
  }
}

export function booksVsServerMeta(value: string | null | undefined): {
  label: string;
  tone: StatusTone;
  detail: string;
  disagree: boolean;
} {
  switch (value) {
    case 'agree':
      return { label: 'Agree', tone: 'active', detail: 'Books and server say the same thing.', disagree: false };
    case 'converging':
      return {
        label: 'Converging',
        tone: 'info',
        detail: 'A suspend or unsuspend job is queued or running; the server will catch up within minutes.',
        disagree: false,
      };
    case 'books_suspended_server_live':
      return {
        label: 'Books suspended, server live',
        tone: 'overdue',
        detail: 'The books want this account suspended but WHM says it is serving, and no job is in flight.',
        disagree: true,
      };
    case 'server_suspended_books_active':
      return {
        label: 'Server suspended, no debt',
        tone: 'overdue',
        detail: 'WHM says suspended but the books show nothing owed (a staff or manual suspension?).',
        disagree: true,
      };
    default:
      return {
        label: 'Unverified',
        tone: 'pending',
        detail: 'No observation from WHM yet.',
        disagree: false,
      };
  }
}

export function desiredReasonLabel(code: string | null | undefined): string {
  switch (code) {
    case 'no_debt': return 'no open suspended invoice';
    case 'dunning': return 'unpaid past grace';
    case 'staff': return 'staff suspension';
    case 'policy_exempt': return 'policy: never suspend';
    case 'arrangement': return 'payment arrangement';
    case 'paused': return 'suspensions paused';
    default: return '—';
  }
}
